import { Component } from 'react';
import { Form, Upload, Button, message } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import { AddFormItemLabel, createFile } from 'utils';
import { DeleteUploadImage, EditUploadImage, UploadPicImage } from 'images';

const FormItem = Form.Item;

const UploadWrapper = styled.div`
  margin-bottom: 24px;
`;

const UploadLabel = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 8px;
  font-weight: 500;

  .required {
    color: #ff4d4f;
    margin-right: 4px;
  }
`;

const RecommendedSize = styled.div`
  color: #666;
  font-size: 14px;
  margin-bottom: 8px;
`;

const CoverImageContainer = styled.div`
  position: relative;
  border: 1px solid #d9d9d9;
  border-radius: 8px;
  overflow: hidden;
  width: 375px;
  max-width: 100%;
  height: 250px;

  &:hover .image-actions {
    opacity: 1;
  }
`;

const CoverImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const ImageActions = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  right: 0;
  left: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  flex-direction: column;
  justify-content: center;
  alignt-items: center;
  padding: 8px;
  opacity: 0;
  transition: opacity 0.3s;
`;

const ActionButton = styled(Button)`
  margin: 0 8px;
  color: white;
  font-size: 16 !important;
  font-weight: 600 !important,
  text-align: center;
  
  .anticon {
    margin-right: 4px;
  }
`;

const EmptyUpload = styled(Upload)`
  .ant-upload {
    max-width: 100%;
    width: ${(props) => props.width}px;
    height: ${(props) => props.height}px;
    border: ${(props) =>
      props.showErrors ? '1px solid #FDA29B' : '1px solid #D5D7DA'};
    border-radius: 8px;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    cursor: pointer;
    transition: border-color 0.3s;

    &:hover {
      border-color: #95bd51;
    }
  }

  .upload-icon {
    color: #95bd51;
    font-size: 24px;
    margin-bottom: 8px;
  }

  .upload-text {
    color: #95bd51;
    font-weight: 500;
  }
`;

const GalleryContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 8px;
`;

const GalleryItem = styled.div`
  position: relative;
  width: 150px;
  height: 100px;
  border-radius: 4px;
  overflow: hidden;

  &:hover .gallery-actions {
    opacity: 1;
  }

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const GalleryActions = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  right: 0;
  left: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 0 0 0 4px;
  opacity: 0;
  transition: opacity 0.3s;
`;

export default class UploadImage extends Component {
  static propTypes = {
    multiple: PropTypes.bool.isRequired,
    label: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    setImage: PropTypes.func.isRequired,
    images: PropTypes.array.isRequired,
    width: PropTypes.string.isRequired,
    height: PropTypes.string.isRequired,
    recommendedSize: PropTypes.string,
    fileList: PropTypes.array,
    showErrors: PropTypes.bool,
  };

  state = {
    loading: false,
  };

  componentDidMount() {
    const { fileList, setImage } = this.props;
    if (fileList && fileList.length > 0) {
      const normalizedFiles = fileList.map((file, index) => ({
        uid: file.uid || `-${index}`, // generate uid if missing
        name: file.name || `image-${index}`,
        status: 'done',
        url: file.url || file.thumbUrl || file, // handle raw string URLs
      }));

      console.log('normalized files on mount:', normalizedFiles);
      setImage(normalizedFiles);
    }
  }

  componentDidUpdate() {
    console.log('images:', this.props.images);
  }

  handleChangeUpload = ({ fileList: newFileList }) => {
    const { setImage, multiple, images } = this.props;

    const mergedFiles = newFileList.map((file) => {
      if (file.originFileObj && !file.url && !file.thumbUrl) {
        return {
          ...file,
          thumbUrl: URL.createObjectURL(file.originFileObj),
        };
      }
      return file;
    });

    setImage(mergedFiles);
  };

  handleBeforeUpload = (file) => {
    const isImage = file.type.startsWith('image/');
    if (!isImage) {
      message.error('Du kannst nur Bilder hochladen!');
      return Upload.LIST_IGNORE;
    }
    return false;
  };

  handleRemove = (uid) => {
    const { setImage, images } = this.props;
    console.log('before remove:', images);
    const newFileList = images.filter((file) => file.uid !== uid);
    console.log('after remove:', newFileList);
    setImage(newFileList, true);
  };

  renderCoverUpload = () => {
    const { loading } = this.state;
    const { name, width, height, showErrors, images } = this.props;

    if (images.length > 0 && images[0]) {
      const file = images[0];
      return (
        <CoverImageContainer>
          <CoverImage src={file.url || file.thumbUrl} alt="Cover" />
          <ImageActions className="image-actions">
            <ActionButton
              icon={
                <img
                  src={EditUploadImage}
                  alt="Edit Image"
                  style={{ height: '20px', width: '20px', marginRight: '6px' }}
                />
              }
              type="text"
              onClick={() => document.getElementById(`upload-${name}`)?.click()}
              style={{ fontSize: '16px', fontWeight: '600' }}
            >
              Bild ändern
            </ActionButton>
            <ActionButton
              icon={
                <img
                  src={DeleteUploadImage}
                  alt="Delete Image"
                  style={{ height: '20px', width: '20px', marginRight: '6px' }}
                />
              }
              type="text"
              onClick={() => this.handleRemove(file.uid)}
              style={{ fontSize: '16px', fontWeight: '600' }}
            >
              Bild entfernen
            </ActionButton>
          </ImageActions>
          <input
            type="file"
            id={`upload-${name}`}
            style={{ display: 'none' }}
            onChange={(e) => {
              if (e.target.files && e.target.files[0]) {
                const file = e.target.files[0];
                const newFile = {
                  uid: `-${Date.now()}`,
                  name: file.name,
                  status: 'done',
                  originFileObj: file,
                  thumbUrl: URL.createObjectURL(file),
                };
                this.handleChangeUpload({ fileList: [newFile] });
              }
            }}
            accept="image/png, image/jpeg, image/webp, image/avif"
          />
        </CoverImageContainer>
      );
    }

    return (
      <EmptyUpload
        name={name}
        width={width ?? '375'}
        height={height ?? '250'}
        showErrors={showErrors}
        listType="picture-card"
        showUploadList={false}
        beforeUpload={this.handleBeforeUpload}
        onChange={this.handleChangeUpload}
        accept="image/png, image/jpeg, image/webp, image/avif"
      >
        <div style={{ display: 'flex', flexDirection: 'row', gap: '4px' }}>
          {loading ? (
            <LoadingOutlined className="upload-icon" />
          ) : (
            <img
              style={{ width: '20px', height: '20px' }}
              src={UploadPicImage}
              alt="upload-img"
            />
          )}
          <div
            style={{ fontSize: '16px', fontWeight: '600', color: '#3F621A' }}
          >
            Bild hochladen
          </div>
        </div>
      </EmptyUpload>
    );
  };

  renderGalleryUpload = () => {
    const { name, width, height, images } = this.props;

    return (
      <>
        <GalleryContainer>
          {images.map((file) => (
            <GalleryItem key={file.uid}>
              <img src={file.url || file.thumbUrl} alt="Gallery" />
              <GalleryActions className="gallery-actions">
                <ActionButton
                  type="text"
                  onClick={() => this.handleRemove(file.uid)}
                  style={{ padding: '4px' }}
                >
                  {
                    <img
                      src={DeleteUploadImage}
                      alt="Delete Image"
                      style={{ height: '20px', width: '20px' }}
                    />
                  }
                </ActionButton>
              </GalleryActions>
            </GalleryItem>
          ))}
          <EmptyUpload
            name={name}
            width={width ?? '375'}
            height={height ?? '250'}
            listType="picture-card"
            showUploadList={false}
            beforeUpload={this.handleBeforeUpload}
            onChange={this.handleChangeUpload}
            fileList={images}
            accept="image/png, image/jpeg, image/webp, image/avif"
            multiple={true}
          >
            <div>
              <img
                style={{ width: '20px', height: '20px' }}
                src={UploadPicImage}
                alt="upload-img"
              />
            </div>
          </EmptyUpload>
        </GalleryContainer>
      </>
    );
  };

  render() {
    const {
      label,
      multiple,
      name,
      showErrors,
      recommendedSize,
      errorTitle,
      images,
    } = this.props;
    const required = !multiple;

    return (
      <UploadWrapper>
        <FormItem
          name={name}
          label={
            multiple ? (
              <UploadLabel>{label}</UploadLabel>
            ) : (
              <AddFormItemLabel>
                <UploadLabel>{label}</UploadLabel>
              </AddFormItemLabel>
            )
          }
          required={false}
          rules={[
            {
              required: required,
              message: `${errorTitle} ist erforderlich.`,
            },
          ]}
          validateStatus={
            showErrors && required && images.length === 0 ? 'error' : ''
          }
          help={
            showErrors && required && images.length === 0
              ? `Bild ist erforderlich.`
              : ''
          }
        >
          <div className="clearfix">
            {!recommendedSize || (
              <RecommendedSize>
                Empfohlene Größe: {recommendedSize} Pixel
              </RecommendedSize>
            )}

            {!multiple ? this.renderCoverUpload() : this.renderGalleryUpload()}
          </div>
        </FormItem>
      </UploadWrapper>
    );
  }
}
